import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  AlertCircle,
  CheckCircle2,
  Building2,
  Calendar,
  ClipboardList,
  Loader2,
  Package,
  PackagePlus,
  RefreshCw,
  Repeat,
  ShoppingBag,
  Plus,
  Send,
  Trash2,
  UserCheck,
} from "lucide-react";

import { auth, db } from "../firebase";
import DeliveryDatePicker from "../components/sampleorders/DeliveryDatePicker";

const STAFF_ROLES = new Set(["owner", "admin", "super_admin", "enduser", "end_user"]);
const INTERNAL_ROLES = new Set(["owner", "admin", "super_admin", "enduser", "end_user"]);
const ORDER_STATUSES = [
  "Pending Approval",
  "Approved",
  "Sent to Warehouse",
  "Acknowledged",
  "Picked",
  "Delivered",
  "Completed",
  "Cancelled",
];
const ORDER_STATUS_FLOW = {
  "Pending Approval": ["Approved", "Cancelled"],
  Approved: ["Sent to Warehouse", "Cancelled"],
  "Sent to Warehouse": ["Acknowledged", "Cancelled"],
  Acknowledged: ["Picked", "Cancelled"],
  Picked: ["Delivered", "Cancelled"],
  Delivered: ["Completed"],
  Completed: [],
  Cancelled: [],
};
const TERMINAL_ORDER_STATUSES = new Set(["Completed", "Cancelled"]);
const DEFAULT_ORDER_WORKFLOW_SETTINGS = {
  onlyOwnersCanApproveOrders: false,
  onlyOwnersCanSendOrdersToWarehouse: false,
  creatorsCanEditApprovedOrders: false,
};
const ORDER_AUDIT_STEPS = [
  { status: "Approved", at: "approvedAt", by: "approvedBy" },
  { status: "Sent to Warehouse", at: "sentToWarehouseAt", by: "sentToWarehouseBy" },
  { status: "Acknowledged", at: "acknowledgedAt", by: "acknowledgedBy" },
  { status: "Picked", at: "pickedAt", by: "pickedBy" },
  { status: "Delivered", at: "deliveredAt", by: "deliveredBy" },
  { status: "Completed", at: "completedAt", by: "completedBy" },
  { status: "Cancelled", at: "cancelledAt", by: "cancelledBy" },
];

const getForwardOrderStatuses = (status) => {
  const current = String(status || "Pending Approval").trim();
  const next = ORDER_STATUS_FLOW[current] || [];
  return [current, ...next];
};

const canManageExistingOrder = ({ isAdmin, isCreator, isAssigned }) =>
  Boolean(isAdmin || isCreator || isAssigned);

const getAllowedOrderStatusOptions = ({
  currentStatus,
  isAdmin,
  canOperate,
  canApprove,
  canSendToWarehouse,
}) => {
  const current = String(currentStatus || "Pending Approval").trim();
  if (current === "Pending Approval" && !canApprove) {
    return isAdmin ? [current, "Cancelled"] : [current];
  }
  if (current === "Approved" && !canSendToWarehouse) {
    return isAdmin ? [current, "Cancelled"] : [current];
  }
  if (isAdmin) return getForwardOrderStatuses(current);
  if (!canOperate) return [current];
  if (current === "Pending Approval") return [current];
  return getForwardOrderStatuses(current).filter((status) => status !== "Cancelled");
};

const normalizeRole = (value) => String(value || "").trim().toLowerCase();
const formatDate = (value) => {
  if (!value) return "—";
  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value?.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString();
  } catch {
    return "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value?.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    return Number.isNaN(date.getTime())
      ? "—"
      : new Intl.DateTimeFormat("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }).format(date);
  } catch {
    return "—";
  }
};

const getOrderActionState = ({
  order,
  meId,
  meRole,
  workflowSettings,
}) => {
  const status = String(order?.status || "Pending Approval").trim();
  const isOwner = meRole === "owner";
  const isAdmin = meRole === "owner" || meRole === "admin" || meRole === "super_admin";
  const isCreator = order?.createdBy === meId;
  const isAssigned = order?.assignedToId === meId;
  const onlyOwnersCanApproveOrders = !!workflowSettings.onlyOwnersCanApproveOrders;
  const onlyOwnersCanSendOrdersToWarehouse = !!workflowSettings.onlyOwnersCanSendOrdersToWarehouse;

  switch (status) {
    case "Pending Approval":
      if (isOwner || (!onlyOwnersCanApproveOrders && isAdmin)) {
        return { label: "Awaiting your approval", tone: "emerald" };
      }
      return {
        label: onlyOwnersCanApproveOrders ? "Waiting on owner approval" : "Pending approval",
        tone: "amber",
      };
    case "Approved":
      if (isOwner || (!onlyOwnersCanSendOrdersToWarehouse && isAdmin)) {
        return { label: "Ready for warehouse handoff", tone: "blue" };
      }
      return {
        label: onlyOwnersCanSendOrdersToWarehouse ? "Waiting on owner handoff" : "Approved",
        tone: "amber",
      };
    case "Sent to Warehouse":
      return { label: isAssigned ? "Awaiting your acknowledgement" : "Warehouse acknowledgement next", tone: "violet" };
    case "Acknowledged":
      return { label: isAssigned ? "Ready to pick" : "Picking next", tone: "violet" };
    case "Picked":
      return { label: isAssigned ? "Ready to deliver" : "Delivery next", tone: "sky" };
    case "Delivered":
      return { label: isAdmin || isAssigned || isCreator ? "Ready to complete" : "Delivered", tone: "sky" };
    case "Completed":
      return { label: "Completed", tone: "slate" };
    case "Cancelled":
      return { label: "Cancelled", tone: "rose" };
    default:
      return { label: status || "Pending Approval", tone: "slate" };
  }
};

const makeEmptyItem = () => ({
  productId: "",
  quantity: 1,
});

const DEFAULT_RECURRING = {
  enabled: false,
  frequency: "weekly",
  interval: 1,
  dayOfWeek: "monday",
  dayOfMonth: 1,
  startDate: "",
  endDate: "",
  ongoing: true,
  note: "",
};

const DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const getInternalUsers = (users = []) =>
  (Array.isArray(users) ? users : []).filter((user) =>
    INTERNAL_ROLES.has(normalizeRole(user?.role))
  );

const getPrice = (inventoryMap, productId) => {
  const item = inventoryMap.get(productId);
  const price = Number(item?.price);
  return Number.isFinite(price) ? price : 0;
};

const getProductName = (inventoryMap, productId) => inventoryMap.get(productId)?.name || "";
const getProductSku = (inventoryMap, productId) => inventoryMap.get(productId)?.sku || "";
const getAccountDisplayName = (account = {}) => account.name || account.accountName || "Untitled account";
const getAccountStreet = (account = {}) =>
  account?.address?.street || account?.address?.line1 || account?.addressStreet || account?.street || "";
const getAccountCity = (account = {}) =>
  account?.address?.city || account?.addressCity || account?.city || "";
const getAccountState = (account = {}) =>
  account?.address?.state || account?.addressState || account?.state || "";
const getAccountZip = (account = {}) =>
  account?.address?.zip || account?.address?.postalCode || account?.addressZip || account?.zip || "";

const TabButton = ({ active, onClick, icon: Icon, label, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onClick()}
    disabled={disabled}
    className={`flex min-w-0 items-center justify-center gap-2 rounded-2xl px-3 py-3 font-medium transition-all ${
      disabled
        ? "cursor-not-allowed text-slate-300"
        : active
        ? "bg-blue-50 text-blue-700 shadow-sm"
        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
    }`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="truncate text-sm">{label}</span>
  </button>
);

const DesktopTabButton = ({ active, onClick, icon: Icon, label, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onClick()}
    disabled={disabled}
    className={`relative flex items-center gap-2 px-4 py-4 font-medium transition-all ${
      disabled
        ? "cursor-not-allowed text-slate-300"
        : active
        ? "bg-blue-50 text-blue-600"
        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
    }`}
  >
    <Icon className="h-4 w-4" />
    <span className="text-sm">{label}</span>
    {active && !disabled ? <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" /> : null}
  </button>
);

const OrdersScreen = () => {
  const [me, setMe] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [orders, setOrders] = useState([]);

  const [activeTab, setActiveTab] = useState("create");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [orderWorkflowSettings, setOrderWorkflowSettings] = useState(DEFAULT_ORDER_WORKFLOW_SETTINGS);

  const [form, setForm] = useState({
    accountId: "",
    requestedDeliveryDate: "",
    assignedToId: "",
    notes: "",
    warehouseNotes: "",
    orderNumber: "",
    items: [makeEmptyItem()],
    recurring: { ...DEFAULT_RECURRING },
  });

  const inventoryMap = useMemo(
    () => new Map((Array.isArray(inventory) ? inventory : []).map((item) => [item.id, item])),
    [inventory]
  );
  const internalUsers = useMemo(() => getInternalUsers(companyUsers), [companyUsers]);
  const userNameMap = useMemo(
    () =>
      new Map(
        (Array.isArray(companyUsers) ? companyUsers : []).map((user) => [
          user.uid || user.id,
          user.fullName || user.displayName || user.name || user.email || "SupplyHawk user",
        ])
      ),
    [companyUsers]
  );
  const currentUser = me;
  const myRole = normalizeRole(me?.role);
  const isOwner = myRole === "owner";
  const isAdmin = myRole === "owner" || myRole === "admin" || myRole === "super_admin";
  const canAssignOthers = isAdmin;
  const activeUsers = internalUsers;
  const onlyOwnersCanApproveOrders = !!orderWorkflowSettings.onlyOwnersCanApproveOrders;
  const onlyOwnersCanSendOrdersToWarehouse = !!orderWorkflowSettings.onlyOwnersCanSendOrdersToWarehouse;
  const creatorsCanEditApprovedOrders = !!orderWorkflowSettings.creatorsCanEditApprovedOrders;

  const resetCreateForm = useCallback(
    (userId = "") => {
      setForm({
        accountId: "",
        requestedDeliveryDate: "",
        assignedToId: myRole === "enduser" || myRole === "end_user" ? userId : "",
        notes: "",
        warehouseNotes: "",
        orderNumber: "",
        items: [makeEmptyItem()],
        recurring: { ...DEFAULT_RECURRING },
      });
    },
    [myRole]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        setMe(null);
        setCompanyId("");
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists()) {
          setError("User profile not found.");
          setLoading(false);
          return;
        }
        const userData = { id: snap.id, uid: user.uid, ...snap.data() };
        const role = normalizeRole(userData.role);
        if (!STAFF_ROLES.has(role)) {
          setError("You do not have access to orders.");
          setLoading(false);
          return;
        }
        setMe(userData);
        setCompanyId(userData.companyId || "");
        resetCreateForm(user.uid);
      } catch (err) {
        console.error("[OrdersScreen] load me failed:", err);
        setError("Failed to load your profile.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [resetCreateForm]);

  useEffect(() => {
    if (!companyId) return undefined;

    const unsubOrders = onSnapshot(
      query(collection(db, "companies", companyId, "orders"), orderBy("updatedAt", "desc")),
      (snap) => {
        const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setOrders(rows);
      },
      (err) => {
        console.error("[OrdersScreen] orders subscribe failed:", err);
        setError("Failed to load orders.");
      }
    );

    const loadStaticData = async () => {
      try {
        const [accountsSnap, inventorySnap, usersSnap, companySnap] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "accounts")),
          getDocs(collection(db, "companies", companyId, "inventory")),
          getDocs(query(collection(db, "users"), where("companyId", "==", companyId))),
          getDoc(doc(db, "companies", companyId)),
        ]);

        setAccounts(
          accountsSnap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
        );

        setInventory(
          inventorySnap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
        );

        setCompanyUsers(
          usersSnap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );

        const featureFlags = companySnap.exists() ? companySnap.data()?.featureFlags || {} : {};
        setOrderWorkflowSettings({
          onlyOwnersCanApproveOrders: !!featureFlags.onlyOwnersCanApproveOrders,
          onlyOwnersCanSendOrdersToWarehouse: !!featureFlags.onlyOwnersCanSendOrdersToWarehouse,
          creatorsCanEditApprovedOrders: !!featureFlags.creatorsCanEditApprovedOrders,
        });
      } catch (err) {
        console.error("[OrdersScreen] supporting loads failed:", err);
        setError("Failed to load order setup data.");
      }
    };

    loadStaticData();
    return () => unsubOrders();
  }, [companyId]);

  useEffect(() => {
    if (!selectedOrderId) {
      setEditForm(null);
      return;
    }
    const order = orders.find((entry) => entry.id === selectedOrderId);
    if (!order) {
      setEditForm(null);
      return;
    }
    setEditForm({
      status: order.status || "Pending Approval",
      assignedToId: order.assignedToId || "",
      requestedDeliveryDate: order.requestedDeliveryDate || "",
      actualDeliveryDate: order.actualDeliveryDate || "",
      notes: order.notes || "",
      warehouseNotes: order.warehouseNotes || "",
      orderNumber: order.orderNumber || "",
    });
  }, [orders, selectedOrderId]);

  const visibleOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter((order) => {
      if (statusFilter !== "all" && order.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        order.accountName,
        order.orderNumber,
        order.status,
        order.createdByName,
        ...(Array.isArray(order.items) ? order.items.map((item) => item.productName) : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [orders, search, statusFilter]);

  const createSummary = useMemo(() => {
    const items = form.items
      .filter((item) => item.productId)
      .map((item) => ({
        ...item,
        price: getPrice(inventoryMap, item.productId),
      }));
    const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const totalValue = items.reduce((sum, item) => sum + Number(item.quantity || 0) * item.price, 0);
    return { totalQty, totalValue };
  }, [form.items, inventoryMap]);

  const selectedOrder = useMemo(
    () => orders.find((entry) => entry.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );
  const selectedOrderStatus = String(selectedOrder?.status || "Pending Approval").trim();
  const isSelectedOrderCreator = Boolean(
    selectedOrder && (selectedOrder.createdBy === (me?.uid || me?.id))
  );
  const isSelectedOrderAssignee = Boolean(
    selectedOrder && selectedOrder.assignedToId === (me?.uid || me?.id)
  );
  const canOperateSelectedOrder = canManageExistingOrder({
    isAdmin,
    isCreator: isSelectedOrderCreator,
    isAssigned: isSelectedOrderAssignee,
  });
  const canApproveSelectedOrder = Boolean(
    selectedOrderStatus === "Pending Approval" &&
      (isOwner || (!onlyOwnersCanApproveOrders && isAdmin))
  );
  const canSendSelectedOrderToWarehouse = Boolean(
    selectedOrderStatus === "Approved" &&
      (isOwner || (!onlyOwnersCanSendOrdersToWarehouse && isAdmin))
  );
  const creatorCanEditSelectedOrderMeta = Boolean(
    creatorsCanEditApprovedOrders &&
      isSelectedOrderCreator &&
      !TERMINAL_ORDER_STATUSES.has(selectedOrderStatus) &&
      selectedOrderStatus !== "Pending Approval"
  );
  const canEditOrderAssignment = Boolean(isAdmin && !TERMINAL_ORDER_STATUSES.has(selectedOrderStatus));
  const canEditOrderContent = Boolean(
    (isAdmin || creatorCanEditSelectedOrderMeta) && !TERMINAL_ORDER_STATUSES.has(selectedOrderStatus)
  );
  const canEditOrderNumber = Boolean(isAdmin && !TERMINAL_ORDER_STATUSES.has(selectedOrderStatus));
  const canAdvanceSelectedOrder = Boolean(
    canOperateSelectedOrder &&
      !TERMINAL_ORDER_STATUSES.has(selectedOrderStatus) &&
      (selectedOrderStatus !== "Pending Approval" || canApproveSelectedOrder) &&
      (selectedOrderStatus !== "Approved" || canSendSelectedOrderToWarehouse)
  );
  const allowedStatusOptions = useMemo(
    () =>
      getAllowedOrderStatusOptions({
        currentStatus: selectedOrderStatus,
        isAdmin,
        canOperate: canAdvanceSelectedOrder,
        canApprove: canApproveSelectedOrder,
        canSendToWarehouse: canSendSelectedOrderToWarehouse,
      }),
    [
      selectedOrderStatus,
      isAdmin,
      canAdvanceSelectedOrder,
      canApproveSelectedOrder,
      canSendSelectedOrderToWarehouse,
    ]
  );
  const currentOrderStatusLocked = !canAdvanceSelectedOrder;
  const selectedOrderAuditTrail = useMemo(() => {
    if (!selectedOrder) return [];
    return ORDER_AUDIT_STEPS.filter((step) => selectedOrder[step.at] || selectedOrder[step.by]).map((step) => ({
      ...step,
      atValue: selectedOrder[step.at] || null,
      byValue: selectedOrder[step.by] || null,
      byName:
        userNameMap.get(selectedOrder[step.by]) ||
        (selectedOrder[`${step.by}Name`] ? selectedOrder[`${step.by}Name`] : null) ||
        "SupplyHawk user",
    }));
  }, [selectedOrder, userNameMap]);
  const selectedAccount = useMemo(
    () => accounts.find((entry) => entry.id === form.accountId) || null,
    [accounts, form.accountId]
  );

  const handleCreateItemChange = (index, field, value) => {
    setForm((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return { ...prev, items: nextItems };
    });
  };

  const handleRecurringChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      recurring: {
        ...prev.recurring,
        [field]: value,
      },
    }));
  };

  const addItemRow = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, makeEmptyItem()] }));
  };

  const removeItemRow = (index) => {
    setForm((prev) => {
      const nextItems = prev.items.filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, items: nextItems.length ? nextItems : [makeEmptyItem()] };
    });
  };

  const refreshLists = async () => {
    if (!companyId) return;
    try {
      const [accountsSnap, inventorySnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "accounts")),
        getDocs(collection(db, "companies", companyId, "inventory")),
      ]);
      setAccounts(accountsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setInventory(inventorySnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error("[OrdersScreen] refresh failed:", err);
    }
  };

  const recurringSummary = useMemo(() => {
    const recurring = form.recurring || DEFAULT_RECURRING;
    if (!recurring.enabled) return "";

    const interval = Number(recurring.interval || 1);
    const everyText =
      interval <= 1
        ? "Every"
        : `Every ${interval} ${recurring.frequency === "monthly" ? "months" : recurring.frequency === "weekly" ? "weeks" : "days"}`;

    if (recurring.frequency === "weekly") {
      const dayLabel =
        DAY_OPTIONS.find((day) => day.value === recurring.dayOfWeek)?.label || "Monday";
      return `${everyText} ${interval <= 1 ? dayLabel : `on ${dayLabel}`}${recurring.startDate ? ` starting ${formatDate(recurring.startDate)}` : ""}`;
    }

    if (recurring.frequency === "monthly") {
      return `${everyText} on day ${recurring.dayOfMonth || 1}${recurring.startDate ? ` starting ${formatDate(recurring.startDate)}` : ""}`;
    }

    return `${everyText}${recurring.startDate ? ` starting ${formatDate(recurring.startDate)}` : ""}`;
  }, [form.recurring]);

  const handleCreateOrder = async (event) => {
    event.preventDefault();
    if (!companyId || !me) return;
    const chosenItems = form.items.filter((item) => item.productId && Number(item.quantity) > 0);
    if (!form.accountId) return setError("Select an account before creating an order.");
    if (!chosenItems.length) return setError("Add at least one order item.");

    const account = accounts.find((entry) => entry.id === form.accountId);
    if (!account) return setError("The selected account could not be found.");

    setSaving(true);
    setError("");
    setSaveMessage("");

    try {
      const normalizedItems = chosenItems.map((item) => ({
        productId: item.productId,
        productName: getProductName(inventoryMap, item.productId),
        sku: getProductSku(inventoryMap, item.productId),
        quantity: Number(item.quantity || 0),
        price: getPrice(inventoryMap, item.productId),
      }));

      const assignedToId =
        myRole === "enduser" || myRole === "end_user"
          ? me.uid || me.id
          : form.assignedToId || "";
      const assignedUser = internalUsers.find(
        (user) => user.uid === assignedToId || user.id === assignedToId
      );
      const recurring = form.recurring || DEFAULT_RECURRING;
      const recurringPayload = recurring.enabled
        ? {
            enabled: true,
            frequency: recurring.frequency,
            interval: Number(recurring.interval || 1),
            dayOfWeek: recurring.frequency === "weekly" ? recurring.dayOfWeek || "monday" : null,
            dayOfMonth: recurring.frequency === "monthly" ? Number(recurring.dayOfMonth || 1) : null,
            startDate: recurring.startDate || form.requestedDeliveryDate || null,
            endDate: recurring.ongoing ? null : recurring.endDate || null,
            ongoing: recurring.ongoing !== false,
            note: recurring.note?.trim() || null,
            summary: recurringSummary || null,
          }
        : null;

      await addDoc(collection(db, "companies", companyId, "orders"), {
        companyId,
        accountId: account.id,
        accountName: account.name || "",
        requestedDeliveryDate: form.requestedDeliveryDate || null,
        actualDeliveryDate: null,
        orderNumber: form.orderNumber.trim() || null,
        status: "Pending Approval",
        items: normalizedItems,
        notes: form.notes.trim() || null,
        warehouseNotes: form.warehouseNotes.trim() || null,
        totalQuantity: normalizedItems.reduce((sum, item) => sum + item.quantity, 0),
        totalValue: normalizedItems.reduce((sum, item) => sum + item.quantity * item.price, 0),
        assignedToId: assignedToId || null,
        assignedToName:
          assignedUser?.fullName || assignedUser?.displayName || assignedUser?.name || assignedUser?.email || null,
        recurring: recurringPayload,
        approvalRequired: true,
        createdBy: me.uid || me.id,
        createdByName: me.fullName || me.displayName || me.email || "SupplyHawk user",
        createdByRole: myRole,
        updatedBy: me.uid || me.id,
        updatedByName: me.fullName || me.displayName || me.email || "SupplyHawk user",
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      setSaveMessage("Order created and routed into approval.");
      resetCreateForm(me.uid || me.id || "");
      setActiveTab("table");
    } catch (err) {
      console.error("[OrdersScreen] create failed:", err);
      setError("Could not create this order.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOrder = async () => {
    if (!companyId || !selectedOrderId || !editForm || !me) return;

    if (!selectedOrder || !canOperateSelectedOrder) {
      setError("You do not have permission to update this order.");
      return;
    }

    const nextStatus = String(editForm.status || selectedOrderStatus).trim();
    if (!allowedStatusOptions.includes(nextStatus)) {
      setError("That status change is not allowed from the current order stage.");
      return;
    }

    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const assignedUser =
        internalUsers.find(
          (user) => user.uid === editForm.assignedToId || user.id === editForm.assignedToId
        ) || null;
      await updateDoc(doc(db, "companies", companyId, "orders", selectedOrderId), {
        status: nextStatus,
        assignedToId: canEditOrderAssignment ? editForm.assignedToId || null : selectedOrder.assignedToId || null,
        assignedToName: canEditOrderAssignment
          ? assignedUser?.fullName || assignedUser?.name || assignedUser?.email || null
          : selectedOrder.assignedToName || null,
        requestedDeliveryDate: canEditOrderContent
          ? editForm.requestedDeliveryDate || null
          : selectedOrder.requestedDeliveryDate || null,
        actualDeliveryDate:
          nextStatus === "Delivered"
            ? editForm.actualDeliveryDate || new Date().toISOString().slice(0, 10)
            : canAdvanceSelectedOrder
            ? editForm.actualDeliveryDate || null
            : selectedOrder.actualDeliveryDate || null,
        notes: canEditOrderContent ? editForm.notes.trim() || null : selectedOrder.notes || null,
        warehouseNotes: editForm.warehouseNotes.trim() || null,
        orderNumber: canEditOrderNumber ? editForm.orderNumber.trim() || null : selectedOrder.orderNumber || null,
        updatedBy: me.uid || me.id,
        updatedByName: me.fullName || me.displayName || me.email || "SupplyHawk user",
        updatedAt: serverTimestamp(),
      });
      setSaveMessage("Order updated.");
    } catch (err) {
      console.error("[OrdersScreen] update failed:", err);
      setError("Could not update this order.");
    } finally {
      setSaving(false);
    }
  };

  const openOrderInTable = (orderId) => {
    setSelectedOrderId(orderId);
    setActiveTab("table");
  };

  if (loading) {
    return (
      <div className="min-h-[55vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading orders…</span>
        </div>
      </div>
    );
  }

  if (!me || !companyId) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
        You do not have access to orders.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-blue-600" />
              <h1 className="text-base font-semibold text-slate-900">Orders</h1>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Create and manage real customer orders with an approval-first workflow.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshLists}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="mt-4 md:hidden">
          <nav className="inline-flex w-full max-w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <div className="grid w-full grid-cols-2 gap-1">
              <TabButton
                active={activeTab === "create"}
                onClick={() => setActiveTab("create")}
                icon={PackagePlus}
                label="Create"
              />
              <TabButton
                active={activeTab === "table"}
                onClick={() => setActiveTab("table")}
                icon={ClipboardList}
                label="Table"
              />
            </div>
          </nav>
        </div>
      </div>

      <div className="hidden border-b border-slate-200 bg-white px-2 md:block md:rounded-2xl md:border md:shadow-sm">
        <nav className="flex overflow-x-auto text-sm">
          <DesktopTabButton
            active={activeTab === "create"}
            onClick={() => setActiveTab("create")}
            icon={PackagePlus}
            label="Create"
          />
          <DesktopTabButton
            active={activeTab === "table"}
            onClick={() => setActiveTab("table")}
            icon={ClipboardList}
            label="Table"
          />
        </nav>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </div>
      ) : null}
      {saveMessage ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            <span>{saveMessage}</span>
          </div>
        </div>
      ) : null}

      {activeTab === "create" ? (
        <div className="grid gap-4 xl:grid-cols-[1.02fr_0.98fr]">
          <form onSubmit={handleCreateOrder} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="space-y-4">
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Orders start in <span className="font-semibold">Pending Approval</span> and then move through warehouse handling
                instead of auto-payment.
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
                  <Package className="h-4 w-4 text-blue-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Order Items</h4>
                </div>

                <div className="space-y-2">
                  {form.items.map((item, index) => {
                    const price = getPrice(inventoryMap, item.productId);
                    const lineTotal = Number(item.quantity || 0) * price;
                    return (
                      <div key={`item-${index}`} className="rounded border border-gray-200 bg-gray-50 p-2.5">
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_120px_auto]">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">Product</label>
                            <select
                              value={item.productId}
                              onChange={(event) => handleCreateItemChange(index, "productId", event.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select item</option>
                              {inventory.map((entry) => (
                                <option key={entry.id} value={entry.id}>
                                  {entry.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">Quantity</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) => handleCreateItemChange(index, "quantity", event.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div className="flex items-end justify-between gap-2 md:justify-end">
                            <div className="text-sm font-medium text-slate-600">${lineTotal.toFixed(2)}</div>
                            <button
                              type="button"
                              onClick={() => removeItemRow(index)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={addItemRow}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add another item
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Customer & Assignment</h4>
                </div>

                <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Account / Customer</label>
                    <select
                      value={form.accountId}
                      onChange={(event) => setForm((prev) => ({ ...prev, accountId: event.target.value }))}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {getAccountDisplayName(account)}
                        </option>
                      ))}
                    </select>

                    {selectedAccount ? (
                      <div className="mt-2 space-y-0.5 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
                        <div className="font-medium text-gray-900">{getAccountDisplayName(selectedAccount)}</div>
                        {selectedAccount.contact && <div>Contact: {selectedAccount.contact}</div>}
                        {selectedAccount.email && <div>Email: {selectedAccount.email}</div>}
                        {selectedAccount.phone && <div>Phone: {selectedAccount.phone}</div>}
                        {[getAccountStreet(selectedAccount), getAccountCity(selectedAccount), getAccountState(selectedAccount), getAccountZip(selectedAccount)]
                          .filter(Boolean)
                          .length ? (
                          <div className="text-gray-600">
                            {[getAccountStreet(selectedAccount), getAccountCity(selectedAccount), getAccountState(selectedAccount), getAccountZip(selectedAccount)]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Assign To{" "}
                      <span className="ml-1 text-[11px] text-gray-500">
                        ({canAssignOthers ? "optional" : "locked to you"})
                      </span>
                    </label>

                    {canAssignOthers ? (
                      <select
                        value={form.assignedToId}
                        onChange={(event) => setForm((prev) => ({ ...prev, assignedToId: event.target.value }))}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not assigned</option>
                        {activeUsers.map((user) => (
                          <option key={user.id} value={user.uid || user.id}>
                            {user.fullName || user.email} ({user.role})
                          </option>
                        ))}
                      </select>
                    ) : currentUser ? (
                      <div className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-900">
                        <UserCheck className="h-4 w-4 text-blue-600" />
                        <span>
                          {currentUser.fullName || currentUser.email}{" "}
                          {currentUser.role ? <span className="text-xs text-gray-500">({currentUser.role})</span> : null}
                        </span>
                      </div>
                    ) : (
                      <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
                        Assignment will be set by an admin.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Scheduling</h4>
                </div>

                <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                  <div>
                    <div className="flex items-center gap-3">
                      <label className="whitespace-nowrap text-xs font-medium text-gray-700">Requested Delivery Date</label>
                      <div className="min-w-0 flex-1">
                        <DeliveryDatePicker
                          value={form.requestedDeliveryDate}
                          onChange={(dateStr) => setForm((prev) => ({ ...prev, requestedDeliveryDate: dateStr }))}
                          deliveryDays={[1, 2, 3, 4, 5]}
                          leadDays={0}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Order Number</label>
                    <input
                      type="text"
                      value={form.orderNumber}
                      onChange={(event) => setForm((prev) => ({ ...prev, orderNumber: event.target.value }))}
                      placeholder="Optional internal order number"
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Repeat className="h-4 w-4 text-blue-600" />
                        <div className="text-sm font-semibold text-slate-900">Recurring Order</div>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Keep this off for one-time orders. Turn it on to save a recurring schedule.
                      </div>
                    </div>

                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!form.recurring?.enabled}
                        onChange={(event) => handleRecurringChange("enabled", event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      Make recurring
                    </label>
                  </div>

                  {form.recurring?.enabled ? (
                    <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                      {recurringSummary ? (
                        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800">
                          {recurringSummary}
                        </div>
                      ) : null}

                      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">Frequency</label>
                          <select
                            value={form.recurring.frequency}
                            onChange={(event) => handleRecurringChange("frequency", event.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Biweekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">Interval</label>
                          <input
                            type="number"
                            min="1"
                            value={form.recurring.interval}
                            onChange={(event) => handleRecurringChange("interval", event.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                        {form.recurring.frequency === "monthly" ? (
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">Day of month</label>
                            <input
                              type="number"
                              min="1"
                              max="31"
                              value={form.recurring.dayOfMonth}
                              onChange={(event) => handleRecurringChange("dayOfMonth", event.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">Day of week</label>
                            <select
                              value={form.recurring.dayOfWeek}
                              onChange={(event) => handleRecurringChange("dayOfWeek", event.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            >
                              {DAY_OPTIONS.map((day) => (
                                <option key={day.value} value={day.value}>
                                  {day.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">Start date</label>
                          <input
                            type="date"
                            value={form.recurring.startDate}
                            onChange={(event) => handleRecurringChange("startDate", event.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">End date</label>
                          <input
                            type="date"
                            value={form.recurring.endDate}
                            onChange={(event) => handleRecurringChange("endDate", event.target.value)}
                            disabled={!!form.recurring.ongoing}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                          />
                        </div>

                        <div className="flex items-end">
                          <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={!!form.recurring.ongoing}
                              onChange={(event) => handleRecurringChange("ongoing", event.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            Ongoing until stopped
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">Recurring note</label>
                        <textarea
                          rows={2}
                          value={form.recurring.note}
                          onChange={(event) => handleRecurringChange("note", event.target.value)}
                          placeholder="Internal scheduling note for this recurring order..."
                          className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
                  <Package className="h-4 w-4 text-blue-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Order Notes</h4>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Customer Notes</label>
                    <textarea
                      rows={2}
                      value={form.notes}
                      onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                      placeholder="Special instructions or requirements..."
                      className="w-full resize-none rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Warehouse Notes</label>
                    <textarea
                      rows={2}
                      value={form.warehouseNotes}
                      onChange={(event) => setForm((prev) => ({ ...prev, warehouseNotes: event.target.value }))}
                      placeholder="Packing, routing, or handling notes..."
                      className="w-full resize-none rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                <div className="font-semibold text-gray-900">Approval summary</div>
                <div className="mt-1">
                  {createSummary.totalQty} items • Estimated value ${createSummary.totalValue.toFixed(2)} • Pending Approval →
                  Approved → Sent to Warehouse → Acknowledged → Picked → Delivered → Completed
                </div>
              </div>
            </div>

            <div className="mt-auto border-t border-gray-200 pt-3 sm:sticky sm:bottom-0 sm:z-10 sm:-mx-4 sm:bg-white/95 sm:px-4 sm:py-3 sm:backdrop-blur sm:supports-[backdrop-filter]:bg-white/85">
              <div className="flex items-center justify-between gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {saving ? "Submitting..." : "Submit Order"}
                </button>
              </div>
            </div>
          </form>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Orders</div>
                <div className="mt-1 text-xs text-slate-500">Compact live view while you create a new order.</div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab("table")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Open table
              </button>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-[1.6fr_1.1fr_0.8fr_0.8fr] gap-3 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <div>Account</div>
                <div>Status</div>
                <div>Qty</div>
                <div>Value</div>
              </div>

              <div className="divide-y divide-slate-200">
                {visibleOrders.length ? (
                  visibleOrders.slice(0, 8).map((order) => (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => openOrderInTable(order.id)}
                      className="grid w-full grid-cols-[1.6fr_1.1fr_0.8fr_0.8fr] gap-3 px-3 py-2 text-left text-sm transition hover:bg-slate-50"
                    >
                      {(() => {
                        const action = getOrderActionState({
                          order,
                          meId: me?.uid || me?.id,
                          meRole: myRole,
                          workflowSettings: orderWorkflowSettings,
                        });
                        const toneClass =
                          action.tone === "emerald"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : action.tone === "amber"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : action.tone === "blue"
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : action.tone === "violet"
                            ? "bg-violet-50 text-violet-700 border-violet-200"
                            : action.tone === "sky"
                            ? "bg-sky-50 text-sky-700 border-sky-200"
                            : action.tone === "rose"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-slate-50 text-slate-700 border-slate-200";
                        return (
                          <>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-900">{order.accountName || "Order"}</div>
                        <div className="truncate text-[11px] text-slate-500">{order.orderNumber || `#${order.id.slice(0, 8)}`}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="truncate text-slate-600">{order.status || "Pending Approval"}</div>
                        <div className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${toneClass}`}>
                          {action.label}
                        </div>
                      </div>
                      <div className="text-slate-600">{order.totalQuantity || 0}</div>
                      <div className="text-slate-600">${Number(order.totalValue || 0).toFixed(2)}</div>
                          </>
                        );
                      })()}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">No orders yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search orders..."
                className="min-w-[220px] flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
              />
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
              >
                <option value="all">All statuses</option>
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 space-y-3">
              {visibleOrders.map((order) => (
                (() => {
                  const action = getOrderActionState({
                    order,
                    meId: me?.uid || me?.id,
                    meRole: myRole,
                    workflowSettings: orderWorkflowSettings,
                  });
                  const toneClass =
                    action.tone === "emerald"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : action.tone === "amber"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : action.tone === "blue"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : action.tone === "violet"
                      ? "bg-violet-50 text-violet-700 border-violet-200"
                      : action.tone === "sky"
                      ? "bg-sky-50 text-sky-700 border-sky-200"
                      : action.tone === "rose"
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-slate-50 text-slate-700 border-slate-200";
                  return (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => setSelectedOrderId(order.id)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                        selectedOrderId === order.id
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">{order.accountName || "Order"}</div>
                          <div className={`mt-1 text-xs ${selectedOrderId === order.id ? "text-slate-300" : "text-slate-500"}`}>
                            {order.orderNumber || `#${order.id.slice(0, 8)}`} • {order.status || "Pending Approval"}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <div className={`text-xs ${selectedOrderId === order.id ? "text-slate-300" : "text-slate-500"}`}>
                              {Array.isArray(order.items) ? order.items.length : 0} items • Qty {order.totalQuantity || 0} • ${Number(order.totalValue || 0).toFixed(2)}
                            </div>
                            <div
                              className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                                selectedOrderId === order.id
                                  ? "border-white/20 bg-white/10 text-white"
                                  : toneClass
                              }`}
                            >
                              {action.label}
                            </div>
                          </div>
                        </div>
                        <div className={`text-xs ${selectedOrderId === order.id ? "text-slate-300" : "text-slate-500"}`}>
                          {formatDate(order.updatedAt || order.createdAt)}
                        </div>
                      </div>
                    </button>
                  );
                })()
              ))}
              {!visibleOrders.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No orders yet.
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {!selectedOrder || !editForm ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Select an order to edit its approval and warehouse flow.
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-base font-semibold text-slate-900">{selectedOrder.accountName || "Order details"}</div>
                  <div className="mt-1 text-sm text-slate-500">
                    {selectedOrder.orderNumber || `#${selectedOrder.id.slice(0, 8)}`} • Created by {selectedOrder.createdByName || "Unknown"}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  {isAdmin
                    ? "Admins and owners can manage approval-first orders, with owner-only restrictions applied from Admin Settings when enabled."
                    : canAdvanceSelectedOrder
                    ? "You can move this order forward through the next allowed warehouse or delivery step, but you cannot reassign it or change the original order details."
                    : canEditOrderContent
                    ? "You can still edit the original order details because creator post-approval edits are enabled for this company."
                    : "This order is view-only for you right now."}
                </div>

                {(selectedOrderStatus === "Pending Approval" && !canApproveSelectedOrder) ||
                (selectedOrderStatus === "Approved" && !canSendSelectedOrderToWarehouse) ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {selectedOrderStatus === "Pending Approval"
                      ? "Only the owner can approve orders for this company right now."
                      : "Only the owner can send approved orders to warehouse for this company right now."}
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</div>
                    <select
                      value={editForm.status}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
                      disabled={currentOrderStatusLocked}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                    >
                      {allowedStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Assigned to</div>
                    <select
                      value={editForm.assignedToId}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, assignedToId: event.target.value }))}
                      disabled={!canEditOrderAssignment}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {internalUsers.map((user) => (
                        <option key={user.id} value={user.uid || user.id}>
                          {user.fullName || user.name || user.email}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Requested delivery</div>
                    <input
                      type="date"
                      value={editForm.requestedDeliveryDate}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, requestedDeliveryDate: event.target.value }))
                      }
                      disabled={!canEditOrderContent}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                    />
                  </label>

                  <label className="block">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Actual delivery</div>
                    <input
                      type="date"
                      value={editForm.actualDeliveryDate}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, actualDeliveryDate: event.target.value }))
                      }
                      disabled={!canAdvanceSelectedOrder}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Order number</div>
                    <input
                      type="text"
                      value={editForm.orderNumber}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, orderNumber: event.target.value }))}
                      disabled={!canEditOrderNumber}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                    />
                  </label>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Items</div>
                  <div className="mt-2 space-y-2">
                    {(Array.isArray(selectedOrder.items) ? selectedOrder.items : []).map((item, index) => (
                      <div key={`${selectedOrder.id}-${index}`} className="flex items-center justify-between gap-3 text-sm text-slate-700">
                        <div>{item.productName || "Item"}</div>
                        <div>Qty {item.quantity || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow Audit</div>
                    <div className="text-[11px] text-slate-400">
                      Last update {formatDateTime(selectedOrder.updatedAt || selectedOrder.createdAt)}
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Created</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">
                        {selectedOrder.createdByName || userNameMap.get(selectedOrder.createdBy) || "Unknown"}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{formatDateTime(selectedOrder.createdAt)}</div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Last status change</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">
                        {selectedOrder.lastStatusChangeBy
                          ? userNameMap.get(selectedOrder.lastStatusChangeBy) || "SupplyHawk user"
                          : "—"}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{formatDateTime(selectedOrder.lastStatusChangeAt)}</div>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2">
                    {selectedOrderAuditTrail.length ? (
                      selectedOrderAuditTrail.map((step) => (
                        <div
                          key={step.status}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                        >
                          <div>
                            <div className="text-sm font-medium text-slate-900">{step.status}</div>
                            <div className="mt-1 text-xs text-slate-500">{step.byName}</div>
                          </div>
                          <div className="text-xs text-slate-500">{formatDateTime(step.atValue)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                        No workflow milestones have been completed yet.
                      </div>
                    )}
                  </div>
                </div>

                <label className="block">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Customer notes</div>
                  <textarea
                    rows={3}
                    value={editForm.notes}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
                    disabled={!canEditOrderContent}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                  />
                </label>

                <label className="block">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Warehouse notes</div>
                  <textarea
                    rows={3}
                    value={editForm.warehouseNotes}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, warehouseNotes: event.target.value }))}
                    disabled={!canAdvanceSelectedOrder}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500"
                  />
                </label>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  {canApproveSelectedOrder ? (
                    <button
                      type="button"
                      onClick={() => setEditForm((prev) => ({ ...prev, status: "Approved" }))}
                      className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                    >
                      Quick approve
                    </button>
                  ) : <div />}
                  <button
                    type="button"
                    disabled={saving || !canAdvanceSelectedOrder}
                    onClick={handleSaveOrder}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                    Save changes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersScreen;
